import React, { useEffect, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import Backgrounds from "../../_partials/Backgrounds/Index";
import TemplateBlock from "../../_partials/TemplateBlock/Index";
import prepareVideoBg from "../../../../shared/functions/prepareVideoBg";
import animationScript, { animationCleanUp } from "./animation";

import assets from "./assets";

import "./style/style.scss";
import "./style/animation.scss";
import getContentById from "../../../../shared/utils/getContentById";
import ImageOrVideo from "../../../../shared/components/ImageOrVideo/Index";
import parseMediaUrl from "../../../../shared/functions/parseMediaUrl";
import TeamLogo from "../../_partials/TeamLogo";
import { MatchScoreWidget } from "../../_partials/MatchScoreWidget/Index";
import Stats from "./stats";
import ProgressBar from "../../../../shared/components/ProgressBar";
import _ from "lodash";
import { getGlobalMatchdays } from "../../../../../services/backend/api.service";
import { getDynamicLogoPath } from "../../../../shared/utils/functions";
import Image from "../../../../shared/components/Image";
import sportWorldShadow from "../../../assets/wc/images/sportworld_image_shadow.png";
import { Item } from "../../../../shared/components/MultipleInput/Item";
import TopPlayerHeader from "./top-player-header";
import { getScaleSize } from "../Lineup/Index";
import Functions from "../../../../shared/components/MediaLibrary/EditModal/functions";
import { getImageFilterStyle, getWrapperDimensions } from "../../../../shared/components/MediaLibrary/EditModal/ImageEditorModal";
import { DEFAULT_ADJUSTMENTS } from "../../../constants/constants";
import { ratioFromFormat } from "../ResultsNew/Index";
import { findRankStatusChangesV2 } from "../../../../shared/functions/standing";
import { UEFAStandingContent } from "./uefaStanding";
import matchdayBundesliga from "./image/matchday_bundesliga.webp";

export const fcbId = "apoawtpvac4zqlancmvw4nk4o";
export const uefaID = "99jev9kv55deht65t6myggxlg";
export const dfbPokalId = "9ttli02frp7wzmw4bns57nevo";
// The standings block, shared verbatim by the template, the SaveModal preview
// and the ImageEditorModal canvas preview. It derives everything it needs from
// `data` so there is exactly one implementation of the row window and the AR
// gradient - the editor preview used to re-implement both and drifted, showing
// the top 5 rather than the window around FCB.
// The table shows every team ranked MAX_RANK or better - rank 11 is never
// printed. A tie at 10th brings in however many teams share it (two, three or
// more), while a shared 9th stops at 10 rows because the rank after 9, 9 is 11.
const MAX_RANK = 10;

// Competition ranking: teams level on BOTH points and goal difference share a
// rank and the next rank skips accordingly - 1, 2, 3, 3, 5, 6, 6, 8, 8, 10, 10.
// `showRank` is false on each repeat so the number is printed once per tie
// group. Ranks are computed over the full table before any rows are dropped.
// goaldifference arrives as a signed string ("+1", "0"), hence parseInt.
const withCompetitionRanks = (rows = []) => {
  const out = [];
  rows.forEach((entry, i) => {
    const prev = out[i - 1];
    const tied =
      prev &&
      Number(prev.points) === Number(entry.points) &&
      parseInt(prev.goaldifference, 10) === parseInt(entry.goaldifference, 10);
    out.push({ ...entry, displayRank: tied ? prev.displayRank : i + 1, showRank: !tied });
  });
  return out;
};

// Every team ranked MAX_RANK or better - no row cap, so a tie at the last
// visible rank is always shown in full.
const takeRows = (ranked) => ranked.filter(r => r.displayRank <= MAX_RANK);

export const StandingSection = ({ data }) => {
  const { dynamic } = data?.data || {};

  const matchInfo = _.get(dynamic, "matchInfo");
  const competitionId = matchInfo?.competition?.id;
  const homeTeam = matchInfo?.contestant?.[0];
  const awayTeam = matchInfo?.contestant?.[1];
  const standing = _.get(dynamic, "standing");

  const findGroup = _.find(standing, (group) => {
    return (
      _.find(group?.ranking, { contestantId: homeTeam?.id }) ||
      _.find(group?.ranking, { contestantId: awayTeam?.id })
    );
  });

  const isUEFA = data?.event?.competitionId === uefaID;
  const selectedLang = data?.lang;
  const isAR = selectedLang === "ar";

  // The top of the table: ranks 1..MAX_RANK.
  const normalRanking = findGroup?.ranking?.length
    ? takeRows(withCompetitionRanks(findGroup.ranking))
    : [];

  const linesMarkersByRankType = findRankStatusChangesV2(findGroup?.ranking || [], []);

  return (
    <>
      {
        isUEFA && <div className={`${isAR ? "gradient-image-ar" : "gradient-image"}`}></div>
      }
      {
        !isUEFA && <div className="table-left-gradient"></div>
      }

      <div className={`wc-content content`}>
        {
          isUEFA ? <UEFAStandingContent
            played={dynamic?.currentWeek}
            content={findGroup?.ranking}
            linesMarkersByRankType={linesMarkersByRankType}
            startingIndex={0}
            className={"first-group"}
            competitionId={competitionId}
            selectedLang={selectedLang}
          /> : <StandingContent
            played={dynamic?.currentWeek}
            content={normalRanking}
            linesMarkersByRankType={''}
            startingIndex={0}
            className={"first-group"}
            competitionId={competitionId}
            selectedLang={selectedLang}
          />
        }

      </div>
    </>
  );
};

const StandingLabel = ({ played, selectedLang }) => {
  const { t } = useTranslation();
  const isAR = selectedLang === "ar";
  return (
    // `table-head-ar` is added alongside `table-head` rather than replacing it
    // (the way the UEFA head does): .group-table .table-head carries the column
    // min-widths here and in both per-format stylesheets, so swapping the class
    // out would drop all of them for Arabic. Style Arabic-only overrides under
    // `.group-table .table-head-ar`.
    <div className={`table-head ${isAR ? "table-head-ar" : ""}`}>
      <div className={"col-pos col"}>{t("Matchday", { lng: selectedLang })} {played}</div>
      <div className={"col-team col"}></div>
      <div className={"col-pkt col"}>{t("PTS", { keyPrefix: "table", lng: selectedLang })}</div>
      <div className={"col-diff col"}>{t("GD", { keyPrefix: "table", lng: selectedLang })}</div>
    </div>
  );
};

export const StandingContent = ({
  played,
  content,
  className,
  startingIndex,
  linesMarkersByRankType,
  competitionId,
  selectedLang
}) => {
  const { t } = useTranslation();
  const redMarkIndex = _.find(linesMarkersByRankType, {
    relegation: true,
  })?.rowIndex;
  const greenMarkIndex = _.findLast(linesMarkersByRankType, {
    relegation: false,
  })?.rowIndex;
  const rowGradientBegin = greenMarkIndex;
  const rowGradientEnd = redMarkIndex;
  const isAR = selectedLang === "ar";
  return (
    <div className={"group-table"}>
      {/* Sits outside .group-content so the bottom fade-out mask applied there
          cannot clip it. Its containing block is .group-table either way (that
          is the positioned ancestor), so the placement is unchanged. */}
      <div className="group-bundesliga-container">
        <img src={matchdayBundesliga} alt="" />
        <span className={isAR ? "bundesliga-table-label-ar" : "bundesliga-table-label"}>
          {t("Table", { keyPrefix: "table", lng: selectedLang })}
        </span>
      </div>
      <div className={isAR ? "group-content-ar" : "group-content"}>
        <StandingLabel played={played} selectedLang={selectedLang} />
        {content.map((entry, index) => {
          const isFCB = entry.contestantId === fcbId;
          const properIndex = index + startingIndex;
          const markRowIndex = _.findIndex(linesMarkersByRankType, {
            rowIndex: properIndex + 1,
          });
          const isMarked = markRowIndex > -1;
          //we need to simulate border top here
          const redMark = properIndex + 2 === redMarkIndex;

          const bottomRedMark = linesMarkersByRankType?.[markRowIndex]?.redLine;

          return (
            <div
              key={"table-" + index}
              className={`table-row ${isFCB ? "table-fcb" : ""} ${index % 2 ? "table-row-even" : ""} ${isMarked ? "mark-row" : ""} ${redMark || bottomRedMark ? "red-mark-row" : ""} ${rowGradientBegin > properIndex || rowGradientEnd <= properIndex + 1 ? "mark-row-gradient" : ""} ${className}`}
            >
              <div className={"col-pos col"}>{entry.showRank ? entry.displayRank : ""}</div>
              <div className={"col-team col"}>
                <div className={"col-logo"}>
                  <Image
                    className="team-logo"
                    src={getDynamicLogoPath({
                      team: {
                        id: entry?.contestantId,
                        code: entry?.contestantCode,
                        name: entry?.contestantName,
                        shortName: entry?.contestantShortName,
                      },
                      competitionId,
                    })}
                  />
                </div>
                <div className="col-team-name">
                  {t(entry?.contestantName || entry?.contestantShortName, {
                    ns: "countries", lng: selectedLang,
                  }) || entry?.contestantName}
                </div>
              </div>
              <div className={"col-pkt col"}>{entry.points}</div>
              <div className={"col-diff col"}>{entry.goaldifference}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getMainBackground = () => {
  return assets.bg_1;
};

const getBlueBg = () => {
  return assets.bg_blue;
};

const Index = ({ animation, data, mode, format, _searchParams, ...props }) => {
  const templateName = data?.template_name ?? "";
  const id = `${templateName}_${data?.uuid}`;
  const prevRatio = props?.prevRatio;
  const backgroundImage = getMainBackground();
  const blueBg = getBlueBg();

  const selectedLang = data?.lang;

  const prepareAnimation = () => {
    prepareVideoBg({ id });
  };

  useEffect(() => {
    let animationReturn = null;
    if (mode === "animation" || mode === "video") {
      if (animation) {
        prepareAnimation();
        animationReturn = animationScript({
          id,
          data,
          format,
          apiSetup: props?.apiSetup,
          templateName: data?.template_name,
        });
      }
    }

    if (mode === "screenshot") {
      const videoBg = $(`#${id} video.custom-background`).get(0);
      if (videoBg) {
        videoBg.addEventListener(
          "loadedmetadata",
          function () {
            this.currentTime = 3;
            this.pause();
          },
          false,
        );
      }
    }

    return function cleanUp() {
      if (animationReturn) {
        animationCleanUp(animationReturn);
      }
    };
  }, [animation, mode]);

  const { options, fields, dynamic, editData } = data.data || {};

  const matchInfo = _.get(dynamic, "matchInfo");
  const competitionId = matchInfo?.competition?.id;

  // The standings rows, their FCB window and the AR gradient all live in
  // StandingSection above - do not re-derive them here.
  const event = data?.event;
  const isUEFA = event.competitionId === uefaID;
  const [layers, setLayers] = useState([]);
  const defaultEffects = {
    isBlur: false,
    isOpacity: false,
    isGradient: false,
    isShadow: false,
    resize: 100,
    ratio: 0,
    ratioUrl: "",
    prevRatio: 0,
  }
  const [effects, setEffects] = useState(defaultEffects)

  useEffect(() => {
    const initialLayers = editData?.layers;
    const initialEffects = editData?.effects;

    if (initialLayers) {
      setTimeout(() => {
        setLayers(initialLayers);
        setEffects(initialEffects ?? defaultEffects);
        if (initialEffects?.ratio > 0) {
          // prevRatioRef.current = initialEffects?.ratio;
        }
      }, 500);
    }
    if (!initialLayers && !editData) {
      const getBackgroundUrls = () => {
        const background = getContentById("background", fields, "self");
        const values = background?.value;
        if (!Array.isArray(values)) return [];

        return values.map((val) => parseMediaUrl(_.get(val, "url", "")));
      }
      const backgroundUrls = getBackgroundUrls();
      const initializeLayers = async () => {
        const newLayerPromises = backgroundUrls.map(async (url, index) => {
          const functions = new Functions(url);
          const finalUrl = functions.getUrl();
          const newDimension = getWrapperDimensions(effects.ratio);
          const newLayer = {
            id: Date.now() + index,
            imageUrl: finalUrl,
            backgroundUrl: url,
            functions,
            dimensions: { width: newDimension.width, height: newDimension.height },
            originalDimensions: { width: newDimension.width, height: newDimension.height },
            nonRatioDimensions: { width: newDimension.width, height: newDimension.height },
            adjustments: { ...DEFAULT_ADJUSTMENTS },
            x: 0,
            y: 0,
            nonRatioX: 0,
            nonRatioY: 0,
            zIndex: index + 1,
            backgroundAndResize: {
              removebg: false,
              removebgUrl: "",
              changebg: false,
              changebgUrl: ""
            },
            isResizing: false,
            isRecreated: false,
          };

          return newLayer;
        });

        const addedLayers = await Promise.all(newLayerPromises);

        setLayers(addedLayers);
        // prevRatioRef.current = 0.56; //default
        setEffects(defaultEffects)
      }
      initializeLayers();
    }
  }, [editData, fields])

  const [ratioSize, setRatioSize] = useState({
    width: 4.075,
    height: 4.075
  });

  useEffect(() => {
    if (prevRatio) {
      setRatioSize(getScaleSize(format, prevRatio));
    } else {
      const editData = data?.data?.editData;
      if (editData) {
        setRatioSize(getScaleSize(format, ratioFromFormat(data.currentFormat)));
      } else {
        setRatioSize(getScaleSize(format, 0));
      }
    }
  }, [format, prevRatio])
  const customBackground = getContentById("background", fields, "self");
  const customBackgroundStyles = _.get(
    customBackground,
    `styles.${format}`,
    {},
  );
  const isAR = selectedLang === "ar";
  return (
    <div
      id={id}
      className={`T_${templateName} ${animation ? "animation" : ""}`}
    >
      <TemplateBlock>
        <div className={"custom-background-container"}>
          <div
            id={_.get(customBackground, "id", null)}
            style={customBackgroundStyles}
            className={"custom-background"}
          >
            {layers
              .map((layer, index) => {
                const { id, adjustments, imageUrl, dimensions, x, y } = layer;
                const filterStyles = getImageFilterStyle(adjustments);
                const { colorOverlays = [], ...filterStyle } = filterStyles;
                return (
                  <div
                    style={{
                      position: 'absolute',
                      width: `${dimensions.width * ratioSize.width}px`,
                      height: `${dimensions.height * ratioSize.height}px`,
                      left: `${x * ratioSize.width}px`,
                      top: `${y * ratioSize.height}px`,
                      ...filterStyle
                    }}
                    className="image-wrapper"
                    key={id}
                  >
                    <img
                      src={imageUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />

                    {/* Color overlays */}
                    {colorOverlays.map((overlayStyle, i) => (
                      <div
                        key={i}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                          ...overlayStyle
                        }}
                      />
                    ))}
                  </div>
                );
              })}
          </div>
        </div>
        <StandingSection data={data} />
      </TemplateBlock>
    </div>
  );
};

export default Index;
